import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  DialogContent,
  DialogActions,
  Snackbar,
  Typography,
  Grid,
  IconButton,
  ButtonGroup,
} from "@mui/material";
import useData from "../hooks/useData";
import SaveIcon from "@mui/icons-material/Save";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPaperPlane } from "@fortawesome/free-solid-svg-icons";

const Reports = ({ schoolId }) => {
  const [mou, setMou] = useState("");
  const [reportLinks, setReportLinks] = useState({
    report_1_link: "",
  });
  const [registrationReports, setRegistrationReports] = useState({
    report_1_name: "",
    report_1_link: "",
    report_2_name: "",
    report_2_link: "",
  });
  const [gradeReports, setGradeReports] = useState({
    report_1_name: "",
    report_1_link: "",
    report_2_name: "",
    report_2_link: "",
  });
  const [restrictedCourses, setRestrictedCourses] = useState({
    course_planning: "",
    rosters_in_progress: "",
    report_1_name: "",
    report_1_link: "",
    report_2_name: "",
    report_2_link: "",
  });
  const [loadingMou, setLoadingMou] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingRegReports, setLoadingRegReports] = useState(false);
  const [loadingGradeReports, setLoadingGradeReports] = useState(false);
  const [loadingRestrictedCourses, setLoadingRestrictedCourses] =
    useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const {
    updateMou,
    fetchMou,
    fetchAppTesting,
    updateAppTesting,
    fetchRegReports,
    updateRegReports,
    fetchGradeReports,
    updateGradeReports,
    fetchRestrictedCourses,
    updateRestrictedCourses,
    sendAdminNotification,
  } = useData();

  useEffect(() => {
    if (schoolId) {
      fetchMou(schoolId).then(setMou).catch(console.error);
      fetchAppTesting(schoolId)
        .then((data) => setReportLinks(data))
        .catch(console.error);
      fetchRegReports(schoolId)
        .then((data) => setRegistrationReports(data))
        .catch(console.error);
      fetchGradeReports(schoolId)
        .then((data) => setGradeReports(data))
        .catch(console.error);
      fetchRestrictedCourses(schoolId)
        .then((data) => setRestrictedCourses(data))
        .catch(console.error);
    }
  }, [schoolId]);

  const sendReportNotification = async (schoolId, reportName) => {
    try {
      // Send notification to school
      const subject = reportName; // Use the reportName for the subject
      const subjectData = {
        jsStruct: JSON.stringify({ subject }),
      };
      await sendAdminNotification(schoolId, subjectData, reportName); // Include reportName
      setSnackbarMessage(`${reportName} notification sent`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error(`Error sending ${reportName} notification:`, error);
    }
  };

  const handleMouChange = (e) => {
    setMou(e.target.value);
  };

  const handleReportLinkChange = (e) => {
    setReportLinks({
      ...reportLinks,
      [e.target.name]: e.target.value,
    });
  };

  const handleRegReportChange = (e) => {
    setRegistrationReports({
      ...registrationReports,
      [e.target.name]: e.target.value,
    });
  };

  const handleGradeReportChange = (e) => {
    setGradeReports({
      ...gradeReports,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdateMou = async () => {
    setLoadingMou(true);
    try {
      const mouData = {
        jsStruct: JSON.stringify({
          mou,
        }),
      };
      await updateMou(schoolId, mouData);
      setSnackbarMessage("MOU has been updated");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating MOU:", error);
    } finally {
      setLoadingMou(false);
    }
  };

  const handleUpdateReports = async () => {
    setLoadingReports(true);
    try {
      const reportData = {
        jsStruct: JSON.stringify(reportLinks),
      };
      await updateAppTesting(schoolId, reportData);
      setSnackbarMessage("Reports have been updated");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating reports:", error);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleUpdateRegReports = async () => {
    setLoadingRegReports(true);
    try {
      const regReportData = {
        jsStruct: JSON.stringify(registrationReports),
      };
      await updateRegReports(schoolId, regReportData);

      setSnackbarMessage("Registration Reports have been updated");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating registration reports:", error);
    } finally {
      setLoadingRegReports(false);
    }
  };

  const handleUpdateGradeReports = async () => {
    setLoadingGradeReports(true);
    try {
      const gradeReportData = {
        jsStruct: JSON.stringify(gradeReports),
      };
      await updateGradeReports(schoolId, gradeReportData);

      setSnackbarMessage("Grade Reports have been updated");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating grade reports:", error);
    } finally {
      setLoadingGradeReports(false);
    }
  };

  const handleRestrictedCoursesChange = (e) => {
    setRestrictedCourses({
      ...restrictedCourses,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdateRestrictedCourses = async () => {
    setLoadingRestrictedCourses(true);
    try {
      const restrictedCoursesData = {
        jsStruct: JSON.stringify(restrictedCourses),
      };
      await updateRestrictedCourses(schoolId, restrictedCoursesData);

      setSnackbarMessage("Restricted Courses have been updated");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating restricted courses:", error);
    } finally {
      setLoadingRestrictedCourses(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const renderTextFieldWithIcon = (label, name, value, onChange) => (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={10}>
        <TextField
          label={label}
          name={name}
          value={value}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={2}>
        {value && ( // Only show icons if a value exists
          <>
            <IconButton
              component="a"
              href={value}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faEye} />
            </IconButton>
            <IconButton
              onClick={() => sendReportNotification(schoolId, label)}
              sx={{ color: "orange" }}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </IconButton>
          </>
        )}
      </Grid>
    </Grid>
  );

  return (
    <>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Applications and Testing Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Applications and Testing
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Application Approval",
                  "report_1_link",
                  reportLinks.report_1_link,
                  handleReportLinkChange,
                )}
              </Grid>
              <Grid item xs={12}>
                <DialogActions>
                  <Button
                    onClick={() => handleUpdateReports(schoolId)}
                    color="success"
                    variant="contained"
                    disabled={loadingReports}
                    startIcon={<SaveIcon />}
                    sx={{
                      mt: 2,
                      color: "white",
                      bgcolor: "success.main",
                      "&:hover": { bgcolor: "success.dark" },
                    }}
                  >
                    {loadingReports
                      ? "Updating Reports..."
                      : "Update App & Testing"}
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </Grid>

          {/* Restricted Courses Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Restricted Courses
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Course Planning",
                  "course_planning",
                  restrictedCourses.course_planning,
                  handleRestrictedCoursesChange,
                )}
              </Grid>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Rosters in Progress",
                  "rosters_in_progress",
                  restrictedCourses.rosters_in_progress,
                  handleRestrictedCoursesChange,
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Report 1 Name"
                  name="report_1_name"
                  value={restrictedCourses.report_1_name}
                  onChange={handleRestrictedCoursesChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Report 1 Link",
                  "report_1_link",
                  restrictedCourses.report_1_link,
                  handleRestrictedCoursesChange,
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Report 2 Name"
                  name="report_2_name"
                  value={restrictedCourses.report_2_name}
                  onChange={handleRestrictedCoursesChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Report 2 Link",
                  "report_2_link",
                  restrictedCourses.report_2_link,
                  handleRestrictedCoursesChange,
                )}
              </Grid>

              <Grid item xs={12}>
                <DialogActions>
                  <Button
                    onClick={() => handleUpdateRestrictedCourses(schoolId)}
                    color="success"
                    variant="contained"
                    disabled={loadingReports}
                    startIcon={<SaveIcon />}
                    sx={{
                      mt: 2,
                      color: "white",
                      bgcolor: "success.main",
                      "&:hover": { bgcolor: "success.dark" },
                    }}
                  >
                    {loadingReports
                      ? "Updating Restricted Courses.."
                      : "Update Restricted Courses"}
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </Grid>

          {/* MOU Section */}
          <Grid item xs={12}>
            {renderTextFieldWithIcon(
              "MOU",
              "mou",
              mou,
              handleMouChange,
              handleUpdateMou,
            )}
          </Grid>
          <Grid item xs={12}>
            <DialogActions>
              <Button
                onClick={() => handleUpdateMou(schoolId)}
                color="success"
                variant="contained"
                disabled={loadingMou}
                startIcon={<SaveIcon />}
                sx={{
                  mt: 2,
                  color: "white",
                  bgcolor: "success.main",
                  "&:hover": { bgcolor: "success.dark" },
                }}
              >
                {loadingMou ? "Updating MOU..." : "Update MOU"}
              </Button>
            </DialogActions>
          </Grid>

          {/* Registration Reports Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Registration Reports
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Registration Report"
                  name="report_1_name"
                  value={registrationReports.report_1_name}
                  onChange={handleRegReportChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Registration Report",
                  "report_1_link",
                  registrationReports.report_1_link,
                  handleRegReportChange,
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Registration Report"
                  name="report_2_name"
                  value={registrationReports.report_2_name}
                  onChange={handleRegReportChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Registration Report",
                  "report_2_link",
                  registrationReports.report_2_link,
                  handleRegReportChange,
                )}
              </Grid>
              <Grid item xs={12}>
                <DialogActions>
                  <Button
                    onClick={() => handleUpdateRegReports(schoolId)}
                    color="success"
                    variant="contained"
                    disabled={loadingRegReports}
                    startIcon={<SaveIcon />}
                    sx={{
                      mt: 2,
                      color: "white",
                      bgcolor: "success.main",
                      "&:hover": { bgcolor: "success.dark" },
                    }}
                  >
                    {loadingRegReports
                      ? "Updating Registration Reports..."
                      : "Update Registration Reports"}
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </Grid>

          {/* Grade Reports Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Grade Reports
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Grade Report 1 Name"
                  name="report_1_name"
                  value={gradeReports.report_1_name}
                  onChange={handleGradeReportChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Grade Report 1 Link",
                  "report_1_link",
                  gradeReports.report_1_link,
                  handleGradeReportChange,
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Grade Report 2 Name"
                  name="report_2_name"
                  value={gradeReports.report_2_name}
                  onChange={handleGradeReportChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                {renderTextFieldWithIcon(
                  "Grade Report 2 Link",
                  "report_2_link",
                  gradeReports.report_2_link,
                  handleGradeReportChange,
                )}
              </Grid>
              <Grid item xs={12}>
                <DialogActions>
                  <Button
                    onClick={() => handleUpdateGradeReports(schoolId)}
                    color="success"
                    variant="contained"
                    disabled={loadingGradeReports}
                    startIcon={<SaveIcon />}
                    sx={{
                      mt: 2,
                      color: "white",
                      bgcolor: "success.main",
                      "&:hover": { bgcolor: "success.dark" },
                    }}
                  >
                    {loadingGradeReports
                      ? "Updating Grade Reports..."
                      : "Update Grade Reports"}
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </>
  );
};

export default Reports;
